/**
 * DataPrepPanel — Transform library browser + recipe management
 * Phase 4.8-B — WQ-198
 */
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { RefreshIcon, SearchIcon } from '../common/Icons';
import { listTransforms, listRecipes, validateRecipe, type Transform, type Recipe, type ValidationResult } from '../../services/dataPrepService';

const DataPrepPanel: React.FC = () => {
  const { authFetch, isLoading: authLoading } = useAuth();
  const [transforms, setTransforms] = useState<Transform[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'transforms' | 'recipes'>('transforms');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [validation, setValidation] = useState<Record<string, ValidationResult>>({});

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [txData, rxData] = await Promise.all([
        listTransforms(authFetch),
        listRecipes(authFetch),
      ]);
      setTransforms(Array.isArray(txData) ? txData : []);
      setRecipes(Array.isArray(rxData) ? rxData : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data prep');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!authLoading) fetchData();
  }, [authLoading]);

  const handleValidate = async (name: string) => {
    try {
      const result = await validateRecipe(authFetch, name);
      setValidation(prev => ({ ...prev, [name]: result }));
    } catch {
      setValidation(prev => ({ ...prev, [name]: { valid: false, errors: ['Validation failed'], warnings: [] } }));
    }
  };

  const uniqueCategories = [...new Set(transforms.map(t => t.category).filter(Boolean))].sort();

  const filteredTransforms = transforms.filter(t =>
    (!searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase()) || t.description?.toLowerCase().includes(searchQuery.toLowerCase())) &&
    (!categoryFilter || t.category === categoryFilter)
  );

  const filteredRecipes = recipes.filter(r =>
    !searchQuery || r.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading && transforms.length === 0) {
    return <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)' }}>Loading data prep...</div>;
  }

  return (
    <div>
      {/* View toggle */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {(['transforms', 'recipes'] as const).map(view => (
          <button key={view} onClick={() => { setActiveView(view); setSearchQuery(''); }} style={{
            fontSize: 13, padding: '6px 16px', borderRadius: 6,
            border: '1px solid var(--border)', cursor: 'pointer', fontWeight: 500,
            background: activeView === view ? 'var(--accent-primary)' : 'var(--bg-secondary)',
            color: activeView === view ? '#fff' : 'var(--text-primary)',
          }}>
            {view === 'transforms' ? `Transforms (${transforms.length})` : `Recipes (${recipes.length})`}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
          <SearchIcon size={16} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input
            type="text" placeholder={`Search ${activeView}...`} value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            style={{ width: '100%', padding: '8px 8px 8px 32px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          />
        </div>
        {activeView === 'transforms' && (
          <select
            value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: 13 }}
          >
            <option value="">All Categories</option>
            {uniqueCategories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <button onClick={fetchData} style={{
          fontSize: 13, padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)',
          fontWeight: 500, cursor: 'pointer', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}>
          <RefreshIcon size={14} /> Refresh
        </button>
      </div>

      {error && <div style={{ padding: 12, background: '#fef2f2', color: '#dc2626', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Transforms view */}
      {activeView === 'transforms' && (
        filteredTransforms.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No transforms found.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {filteredTransforms.map(tx => (
              <div key={tx.name} style={{
                padding: 14, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                  <strong style={{ fontSize: 13 }}>{tx.name}</strong>
                  <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
                    {tx.category}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>{tx.description}</div>
                {tx.parameters && Object.keys(tx.parameters).length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                    Params: {Object.keys(tx.parameters).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {/* Recipes view */}
      {activeView === 'recipes' && (
        filteredRecipes.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)' }}>No recipes found.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)' }}>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Name</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Description</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Steps</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Validation</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipes.map(recipe => {
                  const v = validation[recipe.name];
                  return (
                    <tr key={recipe.name} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 600 }}>{recipe.name}</td>
                      <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
                        {recipe.description || '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>{recipe.steps?.length || 0}</td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        {v ? (
                          <span style={{
                            fontSize: 11, padding: '2px 8px', borderRadius: 10,
                            background: v.valid ? '#dcfce7' : '#fef2f2',
                            color: v.valid ? '#166534' : '#dc2626',
                          }}>
                            {v.valid ? 'Valid' : `${v.errors.length} errors`}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px 12px' }}>
                        <button onClick={() => handleValidate(recipe.name)} style={{
                          fontSize: 11, padding: '3px 8px', borderRadius: 4,
                          border: '1px solid var(--border)', background: 'var(--bg-secondary)',
                          color: 'var(--text-primary)', cursor: 'pointer',
                        }}>
                          Validate
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
};

export default DataPrepPanel;
